import { useEffect, useState } from 'react';
import {
  listarDividendos, detalheTrimestre, simularDividendo,
  listarDeliberacoes, criarDeliberacao
} from '../api';
import { PageHeader, Loading, Empty, Alert, KPI, Badge } from '../components/common';
import { fmtMoeda, fmtData, fmtCnpj, competenciaLabel, mesAtual, anosOpcoes } from '../lib/format';

export default function Dividendos({ clienteId }) {
  const [granularidade, setGranularidade] = useState('mensal');
  const [dados, setDados] = useState({ items: [], limite_isencao_mensal: 50000 });
  const [loading, setLoading] = useState(true);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [msg, setMsg] = useState(null);
  const [drillDown, setDrillDown] = useState(null);
  const [modal, setModal] = useState(null);   // 'simulador' | 'deliberacao'

  const carregar = () => {
    if (!clienteId) return;
    setLoading(true);
    const params = granularidade === 'trimestral' ? { granularidade, ano } : { granularidade };
    listarDividendos(clienteId, params)
      .then(setDados)
      .catch(err => setMsg({ tipo: 'danger', txt: err.message }))
      .finally(() => setLoading(false));
  };

  useEffect(carregar, [clienteId, granularidade, ano]);

  const abrirTrimestre = async (trimestre) => {
    try {
      const r = await detalheTrimestre(clienteId, trimestre);
      setDrillDown(r);
    } catch (err) {
      setMsg({ tipo: 'danger', txt: err.message });
    }
  };

  if (!clienteId) return <Empty title="Selecione um cliente" />;

  // Totalizadores
  const totalIsento = (dados.items || []).reduce(
    (s, x) => s + Number(x.direito_adquirido || 0) + Number(x.isento_50k || 0), 0
  );
  const totalTributado = (dados.items || []).reduce(
    (s, x) => s + Number(x.tributado_bruto || 0), 0
  );
  const totalIRRF = (dados.items || []).reduce(
    (s, x) => s + Number(x.irrf_retido || 0), 0
  );

  return (
    <>
      <PageHeader
        title="Distribuição de Lucros e Dividendos"
        sub="Apuração conforme Lei 15.270/2025 (cota mensal de R$ 50.000 por beneficiário)"
        actions={
          <>
            <button onClick={() => setModal('simulador')}>🧮 Simulador</button>
            <button onClick={() => setModal('deliberacao')}>📋 Nova deliberação</button>
          </>
        }
      />

      {msg && <Alert tipo={msg.tipo}>{msg.txt}</Alert>}

      <div className="kpi-grid">
        <KPI label="Total isento" value={fmtMoeda(totalIsento)} variant="success"
             sub="direito adquirido + limite 50k/mês" />
        <KPI label="Total tributado (bruto)" value={fmtMoeda(totalTributado)} variant="warning"
             sub="acima de R$ 50.000/mês" />
        <KPI label="IRRF retido" value={fmtMoeda(totalIRRF)} variant="danger"
             sub="10% sobre o excedente" />
        <KPI label="Limite mensal" value={fmtMoeda(50000)} sub="por beneficiário" />
      </div>

      <div className="toolbar">
        <div className="flex" style={{ gap: 0 }}>
          <button
            className={granularidade === 'mensal' ? 'primary' : ''}
            onClick={() => setGranularidade('mensal')}
            style={{ borderRadius: '8px 0 0 8px' }}
          >
            Mensal
          </button>
          <button
            className={granularidade === 'trimestral' ? 'primary' : ''}
            onClick={() => setGranularidade('trimestral')}
            style={{ borderRadius: '0 8px 8px 0', borderLeft: 'none' }}
          >
            Trimestral
          </button>
        </div>

        {granularidade === 'trimestral' && (
          <select value={ano} onChange={e => setAno(Number(e.target.value))}>
            {anosOpcoes(5).map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        )}

        <span className="muted small">
          {granularidade === 'trimestral'
            ? '⚠ Cota de R$ 50k continua mensal — esta visão apenas agrupa por trimestre'
            : 'Padrão recomendado pelo escritório'}
        </span>
      </div>

      <div className="card">
        {loading ? <Loading /> : (
          granularidade === 'mensal'
            ? <TabelaMensal items={dados.items} />
            : <TabelaTrimestral items={dados.items} onDrillDown={abrirTrimestre} />
        )}
      </div>

      {drillDown && (
        <Modal title={`Detalhe — ${drillDown.trimestre}`} onClose={() => setDrillDown(null)}>
          <TabelaMensal items={drillDown.items} />
        </Modal>
      )}

      {modal === 'simulador' && (
        <SimuladorDividendo clienteId={clienteId} onClose={() => setModal(null)} />
      )}
      {modal === 'deliberacao' && (
        <CadastroDeliberacao
          clienteId={clienteId}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); setMsg({ tipo: 'success', txt: 'Deliberação cadastrada' }); }}
        />
      )}
    </>
  );
}

function TabelaMensal({ items }) {
  if (!items?.length) return <Empty title="Sem distribuições no período" />;
  return (
    <table className="data">
      <thead>
        <tr>
          <th>Competência</th>
          <th>Beneficiário</th>
          <th className="num">Direito adquirido</th>
          <th className="num">Isento (limite 50k)</th>
          <th className="num">Tributado (bruto)</th>
          <th className="num">IRRF retido</th>
          <th className="num">Total</th>
        </tr>
      </thead>
      <tbody>
        {items.map((r, idx) => (
          <tr key={`${r.per_apur}-${r.beneficiario_doc}-${idx}`}>
            <td><span className="badge badge--neutral">{competenciaLabel(r.per_apur)}</span></td>
            <td>
              <div>{r.beneficiario_nome}</div>
              <div className="small muted">{fmtCnpj(r.beneficiario_doc)}</div>
            </td>
            <td className="num">{fmtMoeda(r.direito_adquirido)}</td>
            <td className="num">{fmtMoeda(r.isento_50k)}</td>
            <td className="num">{fmtMoeda(r.tributado_bruto)}</td>
            <td className="num">{fmtMoeda(r.irrf_retido)}</td>
            <td className="num bold">{fmtMoeda(r.total_bruto)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TabelaTrimestral({ items, onDrillDown }) {
  if (!items?.length) return <Empty title="Sem distribuições no ano" />;
  return (
    <table className="data">
      <thead>
        <tr>
          <th>Trimestre</th>
          <th>Beneficiário</th>
          <th className="num">Direito adquirido</th>
          <th className="num">Isento (limite 50k)</th>
          <th className="num">Total isento</th>
          <th>Prazo Reinf</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {items.map((r, idx) => (
          <tr key={`${r.trimestre}-${r.beneficiario_doc}-${idx}`}>
            <td><span className="badge badge--info">{r.trimestre}</span></td>
            <td>
              <div>{r.beneficiario_nome}</div>
              <div className="small muted">{fmtCnpj(r.beneficiario_doc)}</div>
            </td>
            <td className="num">{fmtMoeda(r.direito_adquirido)}</td>
            <td className="num">{fmtMoeda(r.isento_50k)}</td>
            <td className="num bold">{fmtMoeda(r.total_isento)}</td>
            <td><Badge tipo="warning">{fmtData(r.prazo_envio_reinf)}</Badge></td>
            <td>
              <button className="ghost" onClick={() => onDrillDown(r.trimestre)}>Ver meses</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
    }} onClick={onClose}>
      <div className="card" style={{ width: wide ? 800 : 560, maxHeight: '85vh', overflow: 'auto' }}
           onClick={e => e.stopPropagation()}>
        <div className="flex mb-2">
          <h2 style={{ margin: 0, fontSize: 16 }}>{title}</h2>
          <div className="toolbar__spacer" />
          <button onClick={onClose}>Fechar</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SimuladorDividendo({ clienteId, onClose }) {
  const [doc, setDoc] = useState('');
  const [data, setData] = useState(`${mesAtual()}-15`);
  const [valor, setValor] = useState('');
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState(null);

  const simular = async (e) => {
    e.preventDefault();
    setErro(null);
    try {
      const r = await simularDividendo(clienteId, {
        beneficiario_doc: doc.replace(/\D/g, ''),
        data_lcto: data,
        valor: Number(valor),
      });
      setResultado(r);
    } catch (err) {
      setErro(err.message);
    }
  };

  return (
    <Modal title="Simulador de distribuição" onClose={onClose} wide>
      <form onSubmit={simular}>
        <div className="flex" style={{ gap: 8 }}>
          <input placeholder="CPF/CNPJ do beneficiário" value={doc} onChange={e => setDoc(e.target.value)} required />
          <input type="date" value={data} onChange={e => setData(e.target.value)} required />
          <input type="number" step="0.01" placeholder="Valor líquido" value={valor} onChange={e => setValor(e.target.value)} required />
          <button className="primary" type="submit">Simular</button>
        </div>
      </form>

      {erro && <Alert tipo="danger">{erro}</Alert>}

      {resultado && (
        <div className="mt-2">
          <div className="flex" style={{ gap: 24, fontSize: 12 }}>
            <span>Regime: <Badge tipo={
              resultado.regime === 'tributado' ? 'danger' :
              resultado.regime === 'split' ? 'warning' : 'success'
            }>{resultado.regime}</Badge></span>
            {resultado.acumuladoAntes !== undefined && (
              <span>Acumulado antes: <b>{fmtMoeda(resultado.acumuladoAntes)}</b></span>
            )}
            {resultado.cotaRestante !== undefined && (
              <span>Cota restante: <b>{fmtMoeda(resultado.cotaRestante)}</b></span>
            )}
          </div>

          <table className="data mt-2">
            <thead>
              <tr><th>Tipo</th><th>Fundamento</th><th className="num">Valor</th>
                  <th className="num">Bruto (gross-up)</th><th className="num">IRRF</th></tr>
            </thead>
            <tbody>
              {resultado.partes.map((p, i) => (
                <tr key={i}>
                  <td><Badge tipo={p.tipo === 'isento' ? 'success' : 'warning'}>{p.tipo}</Badge></td>
                  <td className="small">{p.fundamento || '—'}</td>
                  <td className="num">{fmtMoeda(p.valor)}</td>
                  <td className="num">{fmtMoeda(p.vlrBruto)}</td>
                  <td className="num">{fmtMoeda(p.vlrIR)}</td>
                </tr>
              ))}
              <tr className="bold">
                <td colSpan="2">Totais</td>
                <td className="num">{fmtMoeda(resultado.totalLiquido)}</td>
                <td className="num">{fmtMoeda(resultado.totalBruto)}</td>
                <td className="num">{fmtMoeda(resultado.totalIRRF)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

function CadastroDeliberacao({ clienteId, onClose, onSaved }) {
  const [data, setData] = useState('2025-12-15');
  const [comp, setComp] = useState('2025-Q4');
  const [valor, setValor] = useState('');
  const [desc, setDesc] = useState('');
  const [erro, setErro] = useState(null);

  const salvar = async (e) => {
    e.preventDefault();
    setErro(null);
    try {
      await criarDeliberacao(clienteId, {
        data_deliberacao: data,
        competencia_apuracao: comp,
        valor_total_deliberado: Number(valor),
        descricao: desc,
      });
      onSaved();
    } catch (err) {
      setErro(err.message);
    }
  };

  return (
    <Modal title="Nova deliberação de lucros" onClose={onClose}>
      <Alert tipo="info">
        Deliberações até <b>31/12/2025</b> mantêm direito adquirido — lucros distribuídos a partir delas,
        mesmo em 2026 ou depois, são <b>integralmente isentos</b>.
      </Alert>

      <form onSubmit={salvar} className="flex-col">
        <label>Data da deliberação (ata)
          <input type="date" value={data} onChange={e => setData(e.target.value)} required />
        </label>
        <label>Competência apurada
          <input value={comp} onChange={e => setComp(e.target.value)}
                 placeholder="2025-Q4 ou 2025-12" required />
        </label>
        <label>Valor total deliberado
          <input type="number" step="0.01" value={valor} onChange={e => setValor(e.target.value)} required />
        </label>
        <label>Descrição
          <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={3} />
        </label>

        {erro && <Alert tipo="danger">{erro}</Alert>}

        <div className="flex">
          <button type="button" onClick={onClose}>Cancelar</button>
          <button className="primary" type="submit">Salvar deliberação</button>
        </div>
      </form>
    </Modal>
  );
}
